import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Popover,
  TextField,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Search,
  Message,
  History,
  LocalOffer,
  KeyboardReturn,
} from '@mui/icons-material';
import { cannedResponseApi } from '../services/cannedResponseApi';
import { CannedResponse, CannedResponseCategory } from '../types/cannedResponse';
import {
  CannedResponseLang,
  getCannedResponseBody,
  getCannedResponseTitle,
  getStoredCannedLang,
  setStoredCannedLang,
} from '../utils/cannedResponseI18n';

interface CannedResponsePickerProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  onSelect: (content: string) => void;
  placement?: 'top' | 'bottom';
}

const CannedResponsePicker: React.FC<CannedResponsePickerProps> = ({
  anchorEl,
  open,
  onClose,
  onSelect,
  placement = 'top',
}) => {
  const [responses, setResponses] = useState<CannedResponse[]>([]);
  const [categories, setCategories] = useState<CannedResponseCategory[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [insertLang, setInsertLang] = useState<CannedResponseLang>(() => getStoredCannedLang());
  const searchInputRef = useRef<HTMLInputElement>(null);

  const handleInsertLangChange = (_: React.SyntheticEvent, lang: CannedResponseLang | null) => {
    if (lang) {
      setInsertLang(lang);
      setStoredCannedLang(lang);
    }
  };

  // Load responses and categories
  useEffect(() => {
    if (open) {
      setInsertLang(getStoredCannedLang());
      loadData();
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [open]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [responsesData, categoriesData] = await Promise.all([
        cannedResponseApi.getAll({ isActive: true }),
        cannedResponseApi.getCategories(),
      ]);
      setResponses(responsesData);
      setCategories(categoriesData);
    } catch (error) {
      console.error('Failed to load canned responses:', error);
    } finally {
      setLoading(false);
    }
  };

  const searchLower = searchTerm.toLowerCase();
  const filteredResponses = responses.filter(response => {
    const matchesSearch =
      !searchTerm ||
      response.title.toLowerCase().includes(searchLower) ||
      (response.titleAr && response.titleAr.toLowerCase().includes(searchLower)) ||
      response.titleAr?.includes(searchTerm) ||
      response.content.toLowerCase().includes(searchLower) ||
      (response.contentAr && response.contentAr.toLowerCase().includes(searchLower)) ||
      response.contentAr?.includes(searchTerm) ||
      response.shortcut?.toLowerCase().includes(searchLower) ||
      response.shortcutAr?.toLowerCase().includes(searchLower) ||
      response.tags.some(tag => tag.toLowerCase().includes(searchLower));

    const matchesCategory = !selectedCategory || response.category === selectedCategory;

    return matchesSearch && matchesCategory;
  });

  const handleSelect = useCallback(
    async (response: CannedResponse) => {
      onSelect(getCannedResponseBody(response, insertLang));
      await cannedResponseApi.incrementUsage(response.id);
      onClose();
      setSearchTerm('');
      setSelectedCategory(null);
    },
    [insertLang, onSelect, onClose]
  );

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!open) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(prev => (prev < filteredResponses.length - 1 ? prev + 1 : prev));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(prev => (prev > 0 ? prev - 1 : 0));
          break;
        case 'Enter':
          e.preventDefault();
          if (filteredResponses[selectedIndex]) {
            handleSelect(filteredResponses[selectedIndex]);
          }
          break;
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
      }
    },
    [open, filteredResponses, selectedIndex, onClose, handleSelect]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Reset selection when filters change
  useEffect(() => {
    setSelectedIndex(0);
  }, [searchTerm, selectedCategory]);

  const getCategoryColor = (categoryId: string) => {
    const category = categories.find(c => c.id === categoryId);
    return category?.color || '#666';
  };

  const getCategoryName = (categoryId: string) => {
    const category = categories.find(c => c.id === categoryId);
    return category?.name || categoryId;
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: placement === 'top' ? 'top' : 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: placement === 'top' ? 'bottom' : 'top',
        horizontal: 'right',
      }}
      PaperProps={{
        sx: {
          width: 480,
          maxHeight: 500,
          backgroundColor: 'var(--bg-elevated)',
          border: '1px solid var(--border-light)',
          boxShadow: 'var(--shadow-lg)',
          borderRadius: 'var(--radius-lg)',
          overflow: 'hidden',
        },
      }}
    >
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: '1px solid var(--border-light)' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="caption" sx={{ color: 'var(--text-muted)', fontWeight: 600 }}>
            Insert language
          </Typography>
          <ToggleButtonGroup
            value={insertLang}
            exclusive
            size="small"
            onChange={handleInsertLangChange}
            sx={{
              '& .MuiToggleButton-root': {
                px: 1.5,
                py: 0.25,
                fontSize: '12px',
                textTransform: 'none',
                borderColor: 'var(--border-light)',
                color: 'var(--text-secondary)',
              },
              '& .Mui-selected': {
                backgroundColor: 'var(--primary-500) !important',
                color: '#fff !important',
              },
            }}
          >
            <ToggleButton value="en">English</ToggleButton>
            <ToggleButton value="ar" sx={{ fontFamily: 'inherit' }}>
              العربية
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <TextField
          fullWidth
          placeholder="Search EN/AR text, titles, or shortcuts (/greet, /greet_ar)..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          inputRef={searchInputRef}
          variant="outlined"
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: 'var(--text-muted)', fontSize: 20 }} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <Typography variant="caption" color="var(--text-muted)" sx={{ fontSize: '11px' }}>
                  ESC to close
                </Typography>
              </InputAdornment>
            ),
            sx: {
              backgroundColor: 'var(--bg-primary)',
              '& fieldset': {
                borderColor: 'var(--border-light)',
              },
              '&:hover fieldset': {
                borderColor: 'var(--border-medium)',
              },
              '&.Mui-focused fieldset': {
                borderColor: 'var(--primary-500)',
              },
            },
          }}
        />

        {/* Category Filters */}
        <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
          <Chip
            label="All"
            size="small"
            onClick={() => setSelectedCategory(null)}
            color={selectedCategory === null ? 'primary' : 'default'}
            variant={selectedCategory === null ? 'filled' : 'outlined'}
            sx={{
              backgroundColor: selectedCategory === null ? 'var(--primary-500)' : 'transparent',
              color: selectedCategory === null ? 'white' : 'var(--text-secondary)',
              borderColor: 'var(--border-light)',
            }}
          />
          {categories.map((category) => (
            <Chip
              key={category.id}
              label={`${category.name} (${category.responseCount})`}
              size="small"
              onClick={() => setSelectedCategory(category.id)}
              sx={{
                backgroundColor: selectedCategory === category.id ? category.color : 'transparent',
                color: selectedCategory === category.id ? 'white' : 'var(--text-secondary)',
                borderColor: category.color,
                '&:hover': {
                  backgroundColor: category.color,
                  color: 'white',
                },
              }}
            />
          ))}
        </Box>
      </Box>

      {/* Response List */}
      <List sx={{ p: 0, maxHeight: 300, overflow: 'auto' }}>
        {loading ? (
          <ListItem>
            <ListItemText 
              primary="Loading..." 
              sx={{ color: 'var(--text-secondary)', textAlign: 'center' }}
            />
          </ListItem>
        ) : filteredResponses.length === 0 ? (
          <ListItem>
            <ListItemText 
              primary="No responses found" 
              secondary="Try adjusting your search or create a new response"
              slotProps={{
                primary: { sx: { color: 'var(--text-secondary)', textAlign: 'center' } },
                secondary: { sx: { color: 'var(--text-muted)', textAlign: 'center' } }
              }}
            />
          </ListItem>
        ) : (
          filteredResponses.map((response, index) => (
            <React.Fragment key={response.id}>
              <ListItem
                onClick={() => handleSelect(response)}
                sx={{
                  cursor: 'pointer',
                  backgroundColor: index === selectedIndex ? 'var(--bg-selected)' : 'transparent',
                  '&:hover': {
                    backgroundColor: 'var(--bg-hover)',
                  },
                  borderLeft: index === selectedIndex ? `3px solid ${getCategoryColor(response.category)}` : '3px solid transparent',
                  py: 1.5,
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Message sx={{ color: getCategoryColor(response.category), fontSize: 20 }} />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={{
                          color: 'var(--text-primary)',
                          direction: insertLang === 'ar' ? 'rtl' : 'ltr',
                          textAlign: insertLang === 'ar' ? 'right' : 'left',
                        }}
                      >
                        {getCannedResponseTitle(response, insertLang)}
                      </Typography>
                      {response.shortcut && (
                        <Chip
                          label={response.shortcut}
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: '10px',
                            backgroundColor: 'var(--bg-tertiary)',
                            color: 'var(--text-tertiary)',
                            fontFamily: 'var(--font-mono)',
                          }}
                        />
                      )}
                      {response.shortcutAr && (
                        <Chip
                          label={response.shortcutAr}
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: '10px',
                            backgroundColor: 'var(--bg-secondary)',
                            color: 'var(--text-secondary)',
                            fontFamily: 'var(--font-mono)',
                          }}
                        />
                      )}
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'var(--text-secondary)',
                          fontSize: '12px',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          lineHeight: 1.5,
                          mb: 0.5,
                          direction: insertLang === 'ar' ? 'rtl' : 'ltr',
                          textAlign: insertLang === 'ar' ? 'right' : 'left',
                          fontFamily: insertLang === 'ar' ? 'Tahoma, Arial, sans-serif' : 'inherit',
                        }}
                      >
                        {getCannedResponseBody(response, insertLang)}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <Chip
                          label={getCategoryName(response.category)}
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: '10px',
                            backgroundColor: getCategoryColor(response.category) + '20',
                            color: getCategoryColor(response.category),
                          }}
                        />
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'var(--text-muted)', fontSize: '11px' }}>
                          <History sx={{ fontSize: 12 }} />
                          {response.usageCount} uses
                        </Box>
                        {response.tags.slice(0, 2).map((tag) => (
                          <Box
                            key={tag}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.3,
                              color: 'var(--text-muted)',
                              fontSize: '11px',
                            }}
                          >
                            <LocalOffer sx={{ fontSize: 10 }} />
                            {tag}
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  }
                />
                {index === selectedIndex && (
                  <Tooltip title="Press Enter to insert">
                    <IconButton size="small" sx={{ color: 'var(--primary-500)' }}>
                      <KeyboardReturn sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </ListItem>
              {index < filteredResponses.length - 1 && (
                <Divider sx={{ borderColor: 'var(--border-light)' }} />
              )}
            </React.Fragment>
          ))
        )}
      </List>

      {/* Footer */}
      <Box
        sx={{
          p: 1.5,
          borderTop: '1px solid var(--border-light)',
          backgroundColor: 'var(--bg-secondary)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="caption" color="var(--text-muted)">
          {filteredResponses.length} response{filteredResponses.length !== 1 ? 's' : ''}
        </Typography>
        <Typography variant="caption" color="var(--text-muted)" sx={{ textAlign: 'right', maxWidth: 220 }}>
          ↑↓ Navigate • Enter Insert • Shortcuts: /name (EN) • /name_ar (AR)
        </Typography>
      </Box>
    </Popover>
  );
};

export default CannedResponsePicker;
